import './order-detail-confirm.scss';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { dialogActions } from '../slice/dialog-slice';
import { Button } from '../../ui/button/button';
import { XmlStructureOrderInfoData } from '../../../xml/model/xml-structure-model';
import { OrderDetailConfirmView } from './order-detail-confirm-view';
import { useConfirmNameResolvers, ResolveTarget } from './use-confirm-name-resolver';

type Props = {
  // 同 shop_order 配下の親注文一覧
  orders: XmlStructureOrderInfoData[],
  initialOrderId: string,
  // 同 shop_order 配下の全 order-info-data（オプション抽出用）
  allInfoData: XmlStructureOrderInfoData[],
};

export const OrderDetailConfirmDialog = (props: Props) => {
  const { orders, initialOrderId, allInfoData } = props;
  const dispatch = useDispatch();

  const [index, setIndex] = useState(() => {
    const i = orders.findIndex((o) => o.xml.metaModel.id === initialOrderId);
    return i >= 0 ? i : 0;
  });

  // 名前解決は全注文・全オプション分をまとめて先読みする(流用できるレスポンスは親で1回取得して共有)。
  // → ページャ切替時の待ち時間をなくし、同じ商品/カテゴリのレスポンスを重複取得しない。
  const resolveTargets = useMemo<ResolveTarget[]>(() => {
    const targets: ResolveTarget[] = [];
    // 記載箇所(pageType)を持つ商品だけ pageDataTypes を叩く(無い商品では叩かない)。
    const hasPageType = (vm?: { descriptionInfo?: { pageType?: object } }) =>
      Object.keys(vm?.descriptionInfo?.pageType ?? {}).length > 0;
    orders.forEach((o) => {
      const v = o.xml.viewModel;
      const genuine = o.parts?.partsData?.find((p) => p.xml.viewModel?.type === 'main')?.xml.viewModel;
      const myOptions = allInfoData.filter((d) => d.xml.metaModel.parentId === o.xml.metaModel.id);
      // 親の記載箇所は自身が空ならオプションから継承する(convertResponseToOrderData と同じ挙動)ため、
      // 親自身 or いずれかのオプションが pageType を持つ時だけ親分を叩く。
      const parentHasDescriptionLocations = hasPageType(v) || myOptions.some((opt) => hasPageType(opt.xml.viewModel));
      targets.push({
        productNameId: v.item?.id,
        serviceId: v.category?.id,
        productTagId: v.productTagId || undefined,
        templateGroupId: v.designType?.templateGroupId,
        coverId: v.coverId || undefined,
        boardId: v.boardInfo || undefined,
        coverColorId: v.coverColorId || undefined,
        hasAdditionalInfo: Boolean((genuine?.additionalInfo || []).length),
        storedItemLabel: v.item?.name,
        pageTypeParentFlg: parentHasDescriptionLocations ? '1' : undefined,
      });
      myOptions.forEach((opt) => {
        const ov = opt.xml.viewModel;
        // オプションは pageType のみ解決対象。記載箇所を持つオプションだけ追加する。
        // pageDataTypes は serviceId 必須のため、オプション自身のカテゴリ(無ければ親のカテゴリ)を渡す。
        if (ov.item?.id && hasPageType(ov)) {
          targets.push({ productNameId: ov.item.id, serviceId: ov.category?.id || v.category?.id, pageTypeParentFlg: '2' });
        }
      });
    });
    return targets;
  }, [orders, allInfoData]);
  const resolvers = useConfirmNameResolvers(resolveTargets);

  const currentOrder = orders[index];

  const canPrev = index > 0;
  const canNext = index < orders.length - 1;
  const handlerPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const handlerNext = useCallback(() => setIndex((i) => Math.min(orders.length - 1, i + 1)), [orders.length]);
  const handlerClickClose = useCallback(() => {
    dispatch(dialogActions.pop());
  }, []);

  return (
    <div className="dialog_contents order_detail_confirm">
      <div className="dialog_contents__body order_detail_confirm__body">
        <div className="order_detail_confirm__view">
          {currentOrder ? (
            <OrderDetailConfirmView
              order={currentOrder}
              allInfoData={allInfoData}
              resolvers={resolvers}
            />
          ) : null}
        </div>
      </div>
      <div className="dialog_contents__footer order_detail_confirm__footer">
        <div className="order_detail_confirm__pager">
          <Button
            label="←"
            color="light"
            size="sm"
            onClick={handlerPrev}
            disabled={!canPrev}
          />
          <span className="order_detail_confirm__pager__count">
            {index + 1} / {orders.length}
          </span>
          <Button
            label="→"
            color="light"
            size="sm"
            onClick={handlerNext}
            disabled={!canNext}
          />
        </div>
        <Button
          label="閉じる"
          color="light"
          onClick={handlerClickClose}
        />
      </div>
    </div>
  );
};
